'use client';

import type { ClassValue } from 'clsx';
import type { MouseEvent } from 'react';
import {
  ProductAddToCart,
  ProductAddToCartCount,
  ProductAddToCartLabel,
  ProductAddToCartMark,
  ProductAddToCartVariant,
} from '@vs-shop/commerce-ui/productAddToCart';
import { clsxm } from '@vs-shop/utils';
import { shouldAddToCartButtonBeDisabled } from '../../../../../shared/utils/stockValidation';
import { useIntl } from 'react-intl';

import { getDefaultCurrency } from '../../../../../config/currency';
import { purchaseButtonClassName } from '../../../../../config/purchaseButtonClass';
import type { ProductAgeRestriction } from '@vs-shop/schemas/branded/ProductSchema';
import type { ProductImagePreview } from '../../../../../product/productImagePreview';

import type { StockInfo } from '../../../../../product/index';

import { IconCartOutline } from '@vs-shop/ui/icons';
import { IntlMoney } from '../../../../server/intl/Intl';

import { useAddToCart, useVariantInCart } from '../../../../../hook/useAddToCart';
import {
  useCartLinePreviewPreload,
  withCartLinePreview,
} from '../../../../../hook/cartLinePreview';

type Props = {
  productCode: string;
  productName: string;
  imagePreview: ProductImagePreview;
  ageRestriction: ProductAgeRestriction;
  price: number;
  priceWithoutTax: number;
  amount: number;
  stock: StockInfo;
  className?: ClassValue;
};

/*
 * TEMP: allow adding to cart multiple times
 * */
export default function ProductPanelSuggestionsItemAddToCart({
  productCode,
  productName,
  imagePreview,
  ageRestriction,
  price,
  priceWithoutTax,
  amount,
  stock,
  className,
}: Props) {
  const intl = useIntl();
  const currency = getDefaultCurrency();

  const addToCart = useAddToCart();
  const { quantityInCart, isAdding } = useVariantInCart(productCode);
  const isInCart = quantityInCart > 0;
  const imagePath = imagePreview.kind === 'available' ? imagePreview.template : null;
  const cartRequest = withCartLinePreview(
    {
      product: { code: productCode, name: productName, stock, price, priceWithoutTax },
      quantity: amount,
    },
    imagePath,
    ageRestriction
  );
  const preloadHandlers = useCartLinePreviewPreload([cartRequest.preview]);

  const onAddProductToCart = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    addToCart([cartRequest]);
  };

  const isDisabled =
    isAdding ||
    shouldAddToCartButtonBeDisabled(stock.eshop.maxOrderable, quantityInCart, amount, price);

  return (
    <ProductAddToCart
      type="button"
      variant={ProductAddToCartVariant.ROW}
      {...preloadHandlers}
      onClick={(e) => {
        onAddProductToCart(e);
      }}
      loading={isAdding}
      disabled={isDisabled}
      className={clsxm(purchaseButtonClassName, className)}
    >
      <ProductAddToCartMark>
        <IconCartOutline className="size-5 shrink-0" />
        {isInCart ? <ProductAddToCartCount>{quantityInCart}</ProductAddToCartCount> : null}
      </ProductAddToCartMark>
      <ProductAddToCartLabel variant={ProductAddToCartVariant.ROW}>
        {intl.formatMessage(
          {
            id: 'component.client.page.product.panel.productPanelCrossSellingItemAddToCart.buyFor',
            defaultMessage: 'Koupit za {price}',
          },
          {
            price: (
              <span key="price" className="ml-0.5 text-lg/5 font-semibold tracking-wide">
                <IntlMoney value={{ amount: price, currency }} />
              </span>
            ),
          }
        )}
      </ProductAddToCartLabel>
    </ProductAddToCart>
  );
}
